import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image } from '@react-pdf/renderer';
import { renderCharacterBoxes } from '../../../helper/CertificateHelper';

// Área temática del curso: valor fijo para todos los certificados DC-3 generados por esta app.
const DC3_THEMATIC_AREA_CODE = '6000';
const DC3_THEMATIC_AREA_LABEL = 'SEGURIDAD';

const INSTRUCTIONS = [
  '-  Llenar a máquina o con letra de molde.',
  '-  Deberá entregarse al trabajador dentro de los veinte días hábiles siguientes al término del curso de capacitación aprobado.',
  '1/  Las áreas y subáreas ocupacionales del Catálogo Nacional de Ocupaciones se encuentran disponibles en el reverso de este formato y en la página www.stps.gob.mx',
  '2/  Las áreas temáticas de los cursos se encuentran disponibles en el reverso de este formato y en la página www.stps.gob.mx',
  '3/  Cursos impartidos por el área competente de la Secretaria del Trabajo y Previsión Social.',
  '4/  Para empresas con menos de 51 trabajadores. Para empresas con más de 50 trabajadores firmaría el representante del patrón ante la Comisión mixta de capacitación, adiestramiento y productividad.',
  '5/  Solo para empresas con más de 50 trabajadores.',
  '*  Dato no obligatorio.',
];

const styles = StyleSheet.create({
  page: {
    padding: 24,
    fontFamily: 'Helvetica',
    fontSize: 8,
  },
  preview: {
    position: 'absolute',
    width: '100%',
    top: '40%',
    textAlign: 'center',
    fontSize: 80,
    color: 'red',
    transform: 'rotate(-40deg)',
    fontFamily: 'Helvetica-Bold',
    opacity: 0.5,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 0,
  },
  headerTitles: {
    alignItems: 'center',
  },
  logoRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: 6,
  },
  formatTitle: {
    fontSize: 13,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'center',
  },
  mainTitle: {
    fontSize: 13,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'center',
  },
  logo: {
    width: 110,
  },
  sectionTitle: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'center',
    color: '#fff',
    backgroundColor: '#000',
    paddingVertical: 5,
    marginTop: 8,
  },
  fieldLabel: {
    fontSize: 6.5,
    marginBottom: 2,
  },
  fieldBoxFull: {
    borderWidth: 0.5,
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderRightStyle: 'solid',
    borderColor: '#000',
    padding: 3,
    minHeight: 14,
  },
  fieldBoxSmall: {
    padding: 3,
    minHeight: 14,
    minWidth: 40,
  },
  fieldBox: {
    padding: 3,
    minHeight: 14,
    minWidth: 90,
  },
  fieldBoxText: {
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    textTransform: 'uppercase',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  column: {
    flexDirection: 'column',
    marginRight: 24,
    marginBottom: 0,
  },
  inlineLabel: {
    fontSize: 7,
    marginHorizontal: 4,
    alignSelf: 'center',
  },
  dateGroup: {
    flexDirection: 'row',
    alignItems: 'flex-end',
  },
  dateFieldGroup: {
    flexDirection: 'column',
    alignItems: 'center',
    marginRight: 4,
  },
  dateFieldLabel: {
    fontSize: 5.5,
    marginBottom: 1,
    textAlign: 'center',
  },
  legalText: {
    fontSize: 6.5,
    fontFamily: 'Helvetica-Bold',
    marginTop: 10,
    marginBottom: 6,
    textAlign: 'center',
  },
  signaturesRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'stretch',
    marginTop: 26,
  },
  signatureColumn: {
    width: '31%',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  signatureTop: {
    width: '100%',
    alignItems: 'center',
  },
  signatureBottom: {
    width: '100%',
    alignItems: 'center',
  },
  signatureRoleLabel: {
    fontSize: 7,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'center',
    marginBottom: 4,
  },
  signatureImage: {
    position: 'absolute',
    top: 4,
    height: 40,
    alignSelf: 'center',
  },
  signatureName: {
    fontSize: 7.5,
    textAlign: 'center',
    textTransform: 'uppercase',
  },
  signatureLine: {
    borderTopWidth: 0.5,
    borderTopStyle: 'solid',
    borderTopColor: '#000',
    width: '100%',
    marginTop: 6,
  },
  signatureCaption: {
    fontSize: 6.5,
    textAlign: 'center',
    marginTop: 2,
  },
  instructions: {
    marginTop: 14,
  },
  instructionsTitle: {
    fontSize: 7,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 2,
  },
  instructionsLine: {
    fontSize: 6.5,
    marginBottom: 1,
  },
  footerTag: {
    position: 'absolute',
    fontSize: 7,
    textAlign: 'right',
    bottom: 12,
    right: 24,
  },
});

const occupationCode = (student) => {
  const area = student.company?.occupationArea?.key ?? '';
  const subarea = student.company?.occupationSubarea?.key ?? '';
  const code = `${subarea}`.trim();
  return code || '—';
};

// RFC = iniciales (siempre letras, 3 persona moral / 4 persona física) + fecha (6 dígitos) + homoclave (3), separados por guiones.
const renderRfcBoxes = (rfc) => {
  const clean = (rfc ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  let initialsLength = 0;
  while (initialsLength < clean.length && /[A-Z]/.test(clean[initialsLength])) {
    initialsLength++;
  }
  if (initialsLength === 0) {
    initialsLength = 3;
  }
  const totalBoxes = initialsLength + 11;
  const fixedChars = {
    [initialsLength]: '-',
    [initialsLength + 7]: '-',
  };
  return renderCharacterBoxes(clean, totalBoxes, fixedChars);
};

const dateFieldGroup = (label, value, length) => (
  <View style={styles.dateFieldGroup}>
    <Text style={styles.dateFieldLabel}>{label}</Text>
    {renderCharacterBoxes(value, length)}
  </View>
);

const yearMonthDayBoxes = (date) => {
  const year = String(date.getFullYear());
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return (
    <View style={styles.dateGroup}>
      {dateFieldGroup('Año', year, 4)}
      {dateFieldGroup('Mes', month, 2)}
      {dateFieldGroup('Día', day, 2)}
    </View>
  );
};

// Create Document Component
const Certificate4 = ({ trainingDetail, isPreview, signature, instructorSignature, configuration }) => {

  return (
    <Document>
      {
        trainingDetail.students.map((student) => {
          const company = student.company;
          return (
            <Page key={student.id} size="LETTER" orientation="portrait" style={styles.page}>
              {isPreview && <Text style={styles.preview}>PREVIEW</Text>}

              <View style={styles.logoRow}>
                <Image style={styles.logo} src="/assets/forsspa-lower-logo.png" />
              </View>
              <View style={styles.headerRow}>
                <View style={styles.headerTitles}>
                  <Text style={styles.formatTitle}>FORMATO DC-3</Text>
                  <Text style={styles.mainTitle}>CONSTANCIA DE COMPETENCIAS O DE HABILIDADES LABORALES</Text>
                </View>
              </View>

              <Text style={styles.sectionTitle}>DATOS DEL TRABAJADOR</Text>
              <View style={styles.fieldBoxFull}>
                <Text style={styles.fieldLabel}>Nombre (Anotar apellido paterno, apellido materno y nombre(s))</Text>
                <Text style={styles.fieldBoxText}>{`${student.lastname} ${student.name}`.toUpperCase()}</Text>
              </View>

              <View style={{ ...styles.fieldBoxFull, paddingBottom: 0 }}>
                <View style={styles.row}>
                  <View style={styles.column}>
                    <Text style={styles.fieldLabel}>Clave Única de Registro de Población</Text>
                    {renderCharacterBoxes(student.curp, 18)}
                  </View>
                  <View style={styles.column}>
                    <Text style={styles.fieldLabel}>Ocupación específica (Catálogo Nacional de Ocupaciones) 1/</Text>
                    <View style={styles.fieldBox}>
                      <Text style={styles.fieldBoxText}>{occupationCode(student)}</Text>
                    </View>
                  </View>
                </View>
              </View>


              <View style={styles.fieldBoxFull}>
                <Text style={styles.fieldLabel}>Puesto</Text>
                <Text style={styles.fieldBoxText}>{(student.position ?? '').toUpperCase()}</Text>
              </View>

              <Text style={styles.sectionTitle}>DATOS DE LA EMPRESA</Text>
              <View style={styles.fieldBoxFull}>
                <Text style={styles.fieldLabel}>Nombre o Razón Social (En caso de persona física, anotar apellido paterno, apellido materno y nombre(s))</Text>
                <Text style={styles.fieldBoxText}>{(company?.name ?? '').toUpperCase()}</Text>
              </View>

              <View style={{ ...styles.fieldBoxFull, paddingBottom: 0 }}>
                <Text style={styles.fieldLabel}>Registro Federal de Contribuyentes con homoclave (SHCP)</Text>
                {renderRfcBoxes(company?.rfc)}
              </View>

              <Text style={styles.sectionTitle}>DATOS DEL PROGRAMA DE CAPACITACIÓN, ADIESTRAMIENTO Y PRODUCTIVIDAD</Text>

              <View style={styles.fieldBoxFull}>
                <Text style={styles.fieldLabel}>Nombre del curso</Text>
                <Text style={styles.fieldBoxText}>{(trainingDetail.course?.name ?? '').toUpperCase()}</Text>
              </View>

              <View style={{ ...styles.fieldBoxFull, paddingBottom: 0 }}>

                <View style={styles.row}>

                  <View style={{ ...styles.column, width: '50%', marginRight: 0 }}>

                    <Text style={styles.fieldLabel}>Duración en Horas</Text>
                    <View style={styles.fieldBoxSmall}>
                      <Text style={styles.fieldBoxText}>{trainingDetail.totalHours}</Text>
                    </View>

                  </View>
                  <View style={{ ...styles.column, width: '50%', marginRight: 0 }}>

                    <Text style={styles.fieldLabel}>Periodo de ejecución</Text>
                    <View style={styles.row}>
                      <Text style={styles.inlineLabel}>De</Text>
                      {yearMonthDayBoxes(trainingDetail.dateStart)}
                      <Text style={styles.inlineLabel}>a</Text>
                      {yearMonthDayBoxes(trainingDetail.dateEnd)}
                    </View>

                  </View>
                </View>
              </View>


              <View style={styles.fieldBoxFull}>
                <Text style={styles.fieldLabel}>Área temática del curso 2/</Text>
                <View style={styles.row}>
                  <View style={styles.fieldBoxSmall}>
                    <Text style={styles.fieldBoxText}>{DC3_THEMATIC_AREA_CODE}</Text>
                  </View>
                  <Text style={styles.inlineLabel}>{DC3_THEMATIC_AREA_LABEL}</Text>
                </View>
              </View>


              <View style={styles.fieldBoxFull}>
                <Text style={styles.fieldLabel}>Nombre del agente capacitador o STPS 3/</Text>
                <View style={styles.row}>
                  <Text style={styles.fieldBoxText}>{configuration?.agente_capacitador ?? ''}</Text>
                  <View style={{ marginLeft: 6 }}>
                    <Text style={styles.fieldBoxText}>{configuration?.agente_capacitador_rfc ?? ''}</Text>
                  </View>
                </View>
              </View>

              <View style={{  ...styles.fieldBoxFull, padding: 10, marginTop: 10 }}>
                <Text style={styles.legalText}>
                  Los datos se asientan en esta constancia bajo protesta de decir verdad, apercibidos de la responsabilidad en que incurre todo aquel que no se conduce con verdad.
                </Text>

                <View style={styles.signaturesRow}>
                  <View style={styles.signatureColumn}>
                    <View style={styles.signatureTop}>
                      <Text style={styles.signatureRoleLabel}>Instructor o tutor</Text>
                      <Text style={styles.signatureName}>
                        {`${trainingDetail.instructor?.name ?? ''} ${trainingDetail.instructor?.lastname ?? ''}`}
                      </Text>
                      {signature && instructorSignature && <Image style={styles.signatureImage} src={instructorSignature} />}
                    </View>
                    <View style={styles.signatureBottom}>
                      <View style={styles.signatureLine} />
                      <Text style={styles.signatureCaption}>Nombre y firma</Text>
                    </View>
                  </View>
                  <View style={styles.signatureColumn}>
                    <View style={styles.signatureTop}>
                      <Text style={styles.signatureRoleLabel}>Patrón o representante legal 4/</Text>
                      <Text style={styles.signatureName}>{company?.representante ?? ''}</Text>
                    </View>
                    <View style={styles.signatureBottom}>
                      <View style={styles.signatureLine} />
                      <Text style={styles.signatureCaption}>Nombre y firma</Text>
                    </View>
                  </View>
                  <View style={styles.signatureColumn}>
                    <View style={styles.signatureTop}>
                      <Text style={styles.signatureRoleLabel}>Representante de los trabajadores 5/</Text>
                      <Text style={styles.signatureName}>{company?.representanteTrabajadores ?? ''}</Text>
                    </View>
                    <View style={styles.signatureBottom}>
                      <View style={styles.signatureLine} />
                      <Text style={styles.signatureCaption}>Nombre y firma</Text>
                    </View>
                  </View>
                </View>
              </View>


              <View style={styles.instructions}>
                <Text style={styles.instructionsTitle}>INSTRUCCIONES</Text>
                {INSTRUCTIONS.map((line, index) => (
                  <Text key={index} style={styles.instructionsLine}>{line}</Text>
                ))}
              </View>

              <Text style={styles.footerTag}>DC-3{'\n'}ANVERSO</Text>
            </Page>
          );
        })
      }
    </Document>
  )
};

export default Certificate4;
